/** Question-set builder inside CampaignDetail — Phase B of
 * CAMPAIGN_LEADS_QUESTIONS.md.
 *
 * Lists campaign questions, add-question modal with a kind-picker
 * (one of nine), per-row up/down reorder (drag-sort lands later when
 * we wire a real DnD lib — up/down buttons are accessible and ship
 * today). Editing a question label or options is non-destructive:
 * the InspectionAnswer rows carry a snapshot of label+kind so prior
 * sessions stay coherent.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import { COMMENT_KINDS, CONFORMITY_KINDS } from "@/lib/types";
import type { Question, QuestionKind, QuestionOption } from "@/lib/types";


const KINDS: QuestionKind[] = [
  "text",
  "textarea",
  "number",
  "dropdown",
  "radio",
  "checkboxes",
  "boolean",
  "boolean_comment",
  "tristate",
  "tristate_comment",
  "date",
  "rating",
];

const KINDS_NEED_OPTIONS = new Set<QuestionKind>([
  "dropdown",
  "radio",
  "checkboxes",
]);


export function CampaignQuestions({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Question | null>(null);
  const importRef = useRef<HTMLInputElement | null>(null);
  const importQuestions = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post<{
        created: number;
        updated: number;
        warnings: string[];
      }>(ApiPaths.campaignQuestionImport(campaignId), form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-questions", campaignId],
      });
    },
  });
  function onImportPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    importQuestions.mutate(f);
    e.target.value = "";
  }

  const questions = useQuery({
    queryKey: ["campaign-questions", campaignId],
    queryFn: async () => {
      const res = await api.get<Question[]>(
        ApiPaths.campaignQuestions(campaignId),
      );
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const reorder = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api.post<Question[]>(
        ApiPaths.campaignQuestionsReorder(campaignId),
        { ids },
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["campaign-questions", campaignId], data);
    },
  });

  const archive = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(ApiPaths.campaignQuestion(campaignId, id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-questions", campaignId],
      });
    },
  });

  // A plain `<a href>` would resolve against the SPA origin
  // (app.nexbasira.com → 404) and wouldn't attach the Bearer JWT.
  // Mirror the CampaignLeads template-download pattern: fetch via
  // the api client → Blob → synthetic anchor.
  const downloadTemplate = useMutation({
    mutationFn: async () => {
      const res = await api.get(
        ApiPaths.campaignQuestionTemplate(campaignId),
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nexbasira-questions-${campaignId.slice(0, 8)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  function move(index: number, direction: -1 | 1) {
    if (!questions.data) return;
    const next = [...questions.data];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    reorder.mutate(next.map((q) => q.id));
  }

  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("questions.sectionTitle")}
          </h2>
          <p className="text-xs text-nb-text-3">{t("questions.sectionHint")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => downloadTemplate.mutate()}
            disabled={downloadTemplate.isPending}
          >
            {downloadTemplate.isPending
              ? t("common.preparing", "Preparing…")
              : t("questions.downloadCatalog", "Download catalog (.xlsx)")}
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => importRef.current?.click()}
            disabled={importQuestions.isPending}
          >
            {importQuestions.isPending
              ? t("common.uploading", "Uploading…")
              : t("questions.importCatalog", "Import catalog (.xlsx)")}
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={onImportPicked}
          />
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => setShowAdd(true)}
          >
            {t("questions.addButton")}
          </button>
        </div>
      </header>

      {questions.isLoading && (
        <p className="card text-sm text-nb-text-3">{t("common.loading")}</p>
      )}
      {questions.data && questions.data.length === 0 && (
        <p className="card text-sm text-nb-text-3">{t("questions.empty")}</p>
      )}
      {questions.data && questions.data.length > 0 && (
        <ol className="card divide-y divide-nb-border p-0">
          {questions.data.map((q, idx) => (
            <li
              key={q.id}
              className="flex items-start justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-nb-text">
                  <span className="mr-2 font-mono text-xs text-nb-text-3">
                    {idx + 1}.
                  </span>
                  {q.label}
                  {q.required && (
                    <span className="ml-0.5 text-cvp-warn">*</span>
                  )}
                </p>
                <p className="text-xs text-nb-text-3">
                  {t(`questions.kind.${q.kind}`)}
                  {q.help_text && ` · ${q.help_text}`}
                </p>
                {q.options.length > 0 && (
                  <p className="mt-0.5 text-xs text-nb-text-3">
                    {q.options.map((o) => o.label).join(" · ")}
                  </p>
                )}
                {COMMENT_KINDS.includes(q.kind) && (
                  <p className="mt-0.5 text-xs text-nb-text-3">
                    {q.comment_trigger === "yes" ||
                    q.comment_trigger === "no" ||
                    q.comment_trigger === "nsp"
                      ? t("questions.commentTriggerSummary", {
                          answer:
                            q.comment_trigger === "no"
                              ? t("questions.commentTriggerNo")
                              : q.comment_trigger === "nsp"
                                ? t("questions.commentTriggerNsp")
                                : t("questions.commentTriggerYes"),
                        })
                      : t("questions.commentTriggerSummaryAlways")}
                  </p>
                )}
                {q.is_conformity && q.conformity_value && (
                  <p className="mt-0.5 text-xs font-medium text-nb-mint">
                    {t("questions.conformitySummary", {
                      answer:
                        q.conformity_value === "no"
                          ? t("questions.commentTriggerNo", "No")
                          : t("questions.commentTriggerYes", "Yes"),
                      defaultValue: "Conformity check — conform when: {{answer}}",
                    })}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-start gap-1">
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0 || reorder.isPending}
                  aria-label={t("questions.moveUp")}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => move(idx, 1)}
                  disabled={
                    idx === (questions.data?.length ?? 0) - 1 ||
                    reorder.isPending
                  }
                  aria-label={t("questions.moveDown")}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => setEditing(q)}
                >
                  {t("common.edit")}
                </button>
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs text-cvp-danger"
                  onClick={() => {
                    if (window.confirm(t("questions.archiveConfirm"))) {
                      archive.mutate(q.id);
                    }
                  }}
                  disabled={archive.isPending}
                >
                  {t("common.delete")}
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {showAdd && (
        <QuestionFormModal
          campaignId={campaignId}
          mode="create"
          onClose={() => setShowAdd(false)}
        />
      )}
      {editing && (
        <QuestionFormModal
          campaignId={campaignId}
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}


type ModalProps = {
  campaignId: string;
  mode: "create" | "edit";
  initial?: Question;
  onClose: () => void;
};


function QuestionFormModal({
  campaignId,
  mode,
  initial,
  onClose,
}: ModalProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [label, setLabel] = useState(initial?.label ?? "");
  const [helpText, setHelpText] = useState(initial?.help_text ?? "");
  const [kind, setKind] = useState<QuestionKind>(initial?.kind ?? "text");
  const [required, setRequired] = useState(initial?.required ?? false);
  const [optionsRaw, setOptionsRaw] = useState(
    (initial?.options ?? []).map((o) => o.label).join("\n"),
  );
  // Defaults to "always": "Yes / No + comment" should mean there IS a
  // comment field. Gating it on one answer stays available but must be
  // chosen deliberately — accepting the default used to hide the box on
  // the very answer that needed explaining.
  const [commentTrigger, setCommentTrigger] = useState<
    "always" | "yes" | "no" | "nsp"
  >(
    initial?.comment_trigger === "no"
      ? "no"
      : initial?.comment_trigger === "yes"
        ? "yes"
        : initial?.comment_trigger === "nsp"
          ? "nsp"
          : "always",
  );
  const [isConformity, setIsConformity] = useState(
    initial?.is_conformity ?? false,
  );
  const [conformityValue, setConformityValue] = useState<"yes" | "no">(
    initial?.conformity_value === "no" ? "no" : "yes",
  );

  // Dynamic {cf_} variable picker (OPERATOR_OPS #7): the campaign's
  // custom-field variables, inserted into whichever prompt field is
  // focused (label or help text) at the caret.
  const labelRef = useRef<HTMLInputElement>(null);
  const helpRef = useRef<HTMLInputElement>(null);
  const [activeField, setActiveField] = useState<"label" | "help">("label");
  const variables = useQuery({
    queryKey: ["question-variables", campaignId],
    queryFn: async () =>
      (
        await api.get<{ variables: { name: string; label: string }[] }>(
          ApiPaths.campaignQuestionVariables(campaignId),
        )
      ).data.variables,
    staleTime: 60_000,
  });

  function insertVariable(name: string) {
    const token = `{${name}}`;
    const ref = activeField === "label" ? labelRef : helpRef;
    const value = activeField === "label" ? label : helpText;
    const setValue = activeField === "label" ? setLabel : setHelpText;
    const el = ref.current;
    const pos = el?.selectionStart ?? value.length;
    const next = value.slice(0, pos) + token + value.slice(pos);
    setValue(next);
    // Restore focus + place the caret just after the inserted token.
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        const caret = pos + token.length;
        el.setSelectionRange(caret, caret);
      }
    });
  }

  const save = useMutation({
    mutationFn: async () => {
      const options: QuestionOption[] | string[] = KINDS_NEED_OPTIONS.has(kind)
        ? optionsRaw
            .split("\n")
            .map((l) => l.trim())
            .filter((l) => l)
        : [];
      const payload = {
        label: label.trim(),
        help_text: helpText.trim(),
        kind,
        required,
        options,
        comment_trigger: commentTrigger,
        is_conformity: isConformity,
        conformity_value: conformityValue,
      };
      if (mode === "create") {
        const res = await api.post<Question>(
          ApiPaths.campaignQuestions(campaignId),
          payload,
        );
        return res.data;
      }
      const res = await api.patch<Question>(
        ApiPaths.campaignQuestion(campaignId, initial!.id),
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-questions", campaignId],
      });
      onClose();
    },
  });

  const needsOptions = KINDS_NEED_OPTIONS.has(kind);
  const canSubmit =
    label.trim() &&
    (!needsOptions ||
      optionsRaw.split("\n").some((l) => l.trim()));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t(`questions.${mode}Title`)}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel w-full max-w-lg space-y-3">
        <h2 className="text-lg font-semibold">{t(`questions.${mode}Title`)}</h2>
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("questions.labelLabel")}</span>
          <input
            ref={labelRef}
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onFocus={() => setActiveField("label")}
            maxLength={255}
            autoFocus
          />
        </label>
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("questions.helpTextLabel")}</span>
          <input
            ref={helpRef}
            className="input"
            value={helpText}
            onChange={(e) => setHelpText(e.target.value)}
            onFocus={() => setActiveField("help")}
          />
        </label>

        {/* {cf_} variable picker — click to insert into the focused field. */}
        {(variables.data ?? []).length > 0 && (
          <div className="space-y-1 rounded border border-nb-border/60 bg-nb-bg/40 p-2">
            <p className="text-[10px] text-nb-text-3">
              {t(
                "questions.cfPickerHint",
                "Insert a beneficiary field value into the {{field}} field — resolved per beneficiary when the question is shown and on the report.",
                {
                  field:
                    activeField === "label"
                      ? t("questions.labelLabel")
                      : t("questions.helpTextLabel"),
                },
              )}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(variables.data ?? []).map((v) => (
                <button
                  key={v.name}
                  type="button"
                  className="rounded-full border border-nb-border px-2 py-0.5 text-[11px] text-nb-text hover:border-nb-mint hover:text-nb-mint"
                  title={`{${v.name}}`}
                  onClick={() => insertVariable(v.name)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("questions.kindLabel")}</span>
          <select
            className="input"
            value={kind}
            onChange={(e) => {
              const next = e.target.value as QuestionKind;
              setKind(next);
              // "Only on I don't know" exists only where that answer
              // does. Switching to a kind without it would leave the
              // select showing a value it no longer offers — and a
              // comment box the operator could never open. The server
              // normalises this too; doing it here keeps the form
              // honest about what it is about to save.
              if (next !== "tristate_comment" && commentTrigger === "nsp") {
                setCommentTrigger("always");
              }
            }}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`questions.kind.${k}`)}
              </option>
            ))}
          </select>
        </label>
        {needsOptions && (
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>{t("questions.optionsLabel")}</span>
            <textarea
              className="input min-h-[6rem] font-mono text-xs"
              value={optionsRaw}
              onChange={(e) => setOptionsRaw(e.target.value)}
              placeholder={t("questions.optionsPlaceholder")}
            />
          </label>
        )}
        {COMMENT_KINDS.includes(kind) && (
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>{t("questions.commentTriggerLabel")}</span>
            <select
              className="input"
              value={commentTrigger}
              onChange={(e) =>
                setCommentTrigger(
                  e.target.value as "always" | "yes" | "no" | "nsp",
                )
              }
            >
              <option value="always">
                {t("questions.commentTriggerAlways")}
              </option>
              <option value="yes">{t("questions.commentTriggerOnlyYes")}</option>
              <option value="no">{t("questions.commentTriggerOnlyNo")}</option>
              {/* Only offered where an NSP answer exists — on a plain
                  boolean it would reveal a box no answer could open,
                  which is the unreachable-comment bug again. */}
              {kind === "tristate_comment" && (
                <option value="nsp">
                  {t("questions.commentTriggerOnlyNsp")}
                </option>
              )}
            </select>
            <span className="block text-[11px] text-nb-text-3">
              {t("questions.commentTriggerHelp")}
            </span>
          </label>
        )}
        {CONFORMITY_KINDS.includes(kind) && (
          <div className="space-y-2 rounded border border-nb-border/60 bg-nb-bg/40 p-2">
            <label className="flex items-center gap-2 text-sm text-nb-text-2">
              <input
                type="checkbox"
                checked={isConformity}
                onChange={(e) => setIsConformity(e.target.checked)}
              />
              <span>
                {t(
                  "questions.isConformityLabel",
                  "This is a conformity question",
                )}
              </span>
            </label>
            {isConformity && (
              <label className="space-y-1 text-xs text-nb-text-2">
                <span>
                  {t(
                    "questions.conformityValueLabel",
                    "Which answer counts as conform?",
                  )}
                </span>
                <select
                  className="input"
                  value={conformityValue}
                  onChange={(e) =>
                    setConformityValue(e.target.value as "yes" | "no")
                  }
                >
                  <option value="yes">
                    {t("questions.commentTriggerYes", "Yes")}
                  </option>
                  <option value="no">
                    {t("questions.commentTriggerNo", "No")}
                  </option>
                </select>
              </label>
            )}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-nb-text-2">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          <span>{t("questions.requiredLabel")}</span>
        </label>
        {save.isError && (
          <p className="text-sm text-cvp-danger">
            {describeApiError(save.error, t("questions.saveErrorGeneric"))}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={!canSubmit || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
